import * as React from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Grid2,
    Paper,
    Snackbar,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import ModernNavbar from '../setup/ModernNavbar';
import AutoComplete from '../../BuiltInComponent/AutoComplete';
import { useDispatch, useSelector } from 'react-redux';
import { getEmployeeForList } from '../../features/PayrollManagement/employeeSlice';
import { createUser, getUsersList, resetUserManagementState } from '../../features/AdminManagement/userManagementSlice';

const initialForm = {
    empID: '',
    userName: '',
    userPassword: '',
    userStatus: true
};

export default function UserManagement() {
    const dispatch = useDispatch();
    const { empForList, isLoadingEmpAll } = useSelector((state) => state.emp);
    const { users, isLoading, isError, isSuccess, message } = useSelector((state) => state.userManagement);
    const [form, setForm] = React.useState(initialForm);
    const [errors, setErrors] = React.useState({});
    const [toastOpen, setToastOpen] = React.useState(false);

    React.useEffect(() => {
        dispatch(getEmployeeForList());
        dispatch(getUsersList());
    }, [dispatch]);

    React.useEffect(() => {
        if (isSuccess || isError) {
            setToastOpen(true);
        }

        if (isSuccess) {
            setForm(initialForm);
            setErrors({});
            dispatch(getUsersList());
        }
    }, [dispatch, isError, isSuccess]);

    const handleCloseToast = () => {
        setToastOpen(false);
        dispatch(resetUserManagementState());
    };

    const handleEmployeeChange = (data) => {
        if (!data) {
            setForm((prev) => ({ ...prev, empID: '' }));
            return;
        }

        const keys = Object.keys(data);
        setForm((prev) => ({ ...prev, empID: data[keys[0]] }));
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const validate = () => {
        const nextErrors = {};

        if (!form.userName.trim()) {
            nextErrors.userName = 'User name is required.';
        }

        if (!form.userPassword.trim()) {
            nextErrors.userPassword = 'Password is required.';
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!validate()) {
            return;
        }

        await dispatch(createUser({
            empID: form.empID || null,
            userName: form.userName.trim(),
            userPassword: form.userPassword,
            userStatus: form.userStatus ? 1 : 0
        }));
    };

    return (
        <>
            <ModernNavbar />
            <Container sx={{ mt: 10, pb: 4 }}>
                <Typography sx={{ fontSize: 30, fontWeight: 'bolder', mb: 3 }}>User Management</Typography>

                <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
                    <Typography sx={{ fontSize: 20, fontWeight: 700, mb: 2 }}>Create User</Typography>
                    <Box component="form" onSubmit={handleSubmit}>
                        <Grid2 container spacing={2}>
                            <Grid2 size={4}>
                                <AutoComplete
                                    listData={empForList || []}
                                    value={(empForList || []).find((row) => row.empID === form.empID) || null}
                                    onChange={handleEmployeeChange}
                                    label="Link Employee (Optional)"
                                    disabledValue={isLoadingEmpAll}
                                />
                            </Grid2>
                            <Grid2 size={3}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="User Name"
                                    name="userName"
                                    value={form.userName}
                                    onChange={handleInputChange}
                                    error={Boolean(errors.userName)}
                                    helperText={errors.userName || 'Unique login name'}
                                />
                            </Grid2>
                            <Grid2 size={3}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="password"
                                    label="Password"
                                    name="userPassword"
                                    value={form.userPassword}
                                    onChange={handleInputChange}
                                    error={Boolean(errors.userPassword)}
                                    helperText={errors.userPassword || 'Initial login password'}
                                />
                            </Grid2>
                            <Grid2 size={2}>
                                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                                    <Switch
                                        checked={form.userStatus}
                                        onChange={(event) => setForm((prev) => ({ ...prev, userStatus: event.target.checked }))}
                                    />
                                    <Typography>{form.userStatus ? 'Active' : 'Inactive'}</Typography>
                                </Box>
                            </Grid2>
                            <Grid2 size={12}>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                    <Button variant="outlined" onClick={() => { setForm(initialForm); setErrors({}); }} disabled={isLoading}>
                                        Reset
                                    </Button>
                                    <Button variant="contained" type="submit" disabled={isLoading}>
                                        {isLoading ? 'Saving...' : 'Create User'}
                                    </Button>
                                </Box>
                            </Grid2>
                        </Grid2>
                    </Box>
                </Paper>

                <Paper sx={{ p: 3, borderRadius: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ fontSize: 20, fontWeight: 700 }}>Existing Users</Typography>
                        <Button variant="outlined" onClick={() => dispatch(getUsersList())} disabled={isLoading}>
                            Refresh
                        </Button>
                    </Box>

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>User ID</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>User Name</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Employee</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Department</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Unit</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            <Typography color="textSecondary">No users found.</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : users.map((row) => (
                                    <TableRow key={row.userID} hover>
                                        <TableCell>{row.userID}</TableCell>
                                        <TableCell>{row.userName}</TableCell>
                                        <TableCell>{row.empName || '-'}</TableCell>
                                        <TableCell>{row.deptName || '-'}</TableCell>
                                        <TableCell>{row.unitDescription || '-'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={row.userStatus ? 'Active' : 'Inactive'}
                                                color={row.userStatus ? 'success' : 'default'}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Snackbar
                    open={toastOpen}
                    autoHideDuration={3500}
                    onClose={handleCloseToast}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={handleCloseToast} severity={isError ? 'error' : 'success'} sx={{ width: '100%' }}>
                        {message || (isError ? 'Unable to complete request.' : 'Done.')}
                    </Alert>
                </Snackbar>
            </Container>
        </>
    );
}